/**
 * The original recording's media element, reachable without going through React state.
 *
 * The scheduler has to read the playhead far more often than the interface needs to re-render —
 * a cue boundary missed by 100ms is audible, a timecode late by 100ms is not. Publishing the
 * position through context at scheduler speed would re-render every track in the folder dozens of
 * times a second; reading the element directly costs nothing and is more accurate besides, since
 * it is the element, not a copy of it sampled some time ago.
 *
 * AudioTrackOriginal owns the element and registers it here; useVoicingSchedule reads it.
 */

let element: HTMLMediaElement | null = null;

/**
 * Swallow the rejection play() reports when a pause lands during startup.
 *
 * Stopping the lecturer the instant a voicing must start means play() and pause() legitimately
 * meet in the same frame, and the interrupted play() promise rejects with AbortError. Nothing is
 * wrong then: the pause IS the intended outcome. The guard lives on the element because not every
 * caller is ours. react-waves calls play() from componentWillReceiveProps, unhandled, and it can
 * do so before any of our wavesurfer-level wrappers exist. Anything that is not an AbortError is
 * still surfaced.
 */
const guardPlay = (media: HTMLMediaElement): void => {
    const el = media as HTMLMediaElement & { __playGuarded?: boolean };
    if (el.__playGuarded) return;
    el.__playGuarded = true;
    const originalPlay = media.play.bind(media);
    media.play = () => {
        const started = originalPlay();
        if (started && typeof started.catch === "function") {
            started.catch((err: unknown) => {
                if (!(err instanceof DOMException) || err.name !== "AbortError") {
                    console.error(err);
                }
            });
        }
        return started;
    };
};

export const registerOriginalMedia = (media: HTMLMediaElement | null): void => {
    element = media;
    if (media) guardPlay(media);
};

/**
 * The original's element, registered or found.
 *
 * Registration happens when wavesurfer hands its instance over, and the element is not always in
 * the document by then — measured live, it registered null and never retried, so the schedule had
 * no playhead to read and not one voicing played while the original ran. Falling back to the
 * document removes that ordering dependency: the voicings are tagged `data-cue`, the original is
 * the one that is not.
 */
export const getOriginalMedia = (): HTMLMediaElement | null => {
    if (element && element.isConnected) return element;
    element = document.querySelector<HTMLAudioElement>("audio:not([data-cue])");
    if (element) guardPlay(element);
    return element;
};

/** Playhead in milliseconds, or null when the original is not loaded yet. */
export const originalPositionMs = (): number | null => {
    const media = getOriginalMedia();
    if (!media || !Number.isFinite(media.currentTime)) return null;
    return media.currentTime * 1000;
};

/**
 * Move the lecturer's playhead now, without going through React.
 *
 * A gesture that sets `playStart` and waits for an effect to seek is a gesture the schedule sees
 * late: it reads the element, and for the tick or two before the seek lands the element still
 * reports the OLD position. That is enough for the schedule to conclude the cue is already under
 * way and enter it part-way through instead of at its first word.
 */
export const seekOriginalTo = (ms: number): boolean => {
    const media = getOriginalMedia();
    if (!media || !Number.isFinite(ms) || ms < 0) return false;
    try {
        media.currentTime = ms / 1000;
        return true;
    } catch {
        return false;
    }
};
